import React, { useRef, useState } from 'react';
import { usePublisher } from '@mdxeditor/editor';
import { insertImage$ } from '@mdxeditor/editor';
import { useUploadFile } from '@/api/files/useUploadFile';

const ACCEPTED_MIME_TYPES = 'image/jpeg,image/png,image/webp,image/gif';

/**
 * Toolbar button that opens the OS file picker, uploads the selected image to
 * the backend, and inserts it into the MDXEditor via `insertImage$`.
 *
 * Must be rendered inside the MDXEditor plugin context (i.e. inside
 * toolbarContents) so that `usePublisher` has access to the realm.
 */
const InsertImageButton: React.FC = () => {
  const insertImage   = usePublisher(insertImage$);
  const { uploadFile } = useUploadFile();
  const fileInputRef  = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset so the same file can be re-selected later
    e.target.value = '';

    setIsUploading(true);
    try {
      const url = await uploadFile(file);
      insertImage({ src: url, altText: '' });
    } catch {
      // useUploadFile logs the error and notifies the user.
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={handleButtonClick}
        disabled={isUploading}
        className="p-2 rounded transition-colors hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400 disabled:opacity-50"
        title="Insert Image"
        aria-label="Insert Image"
      >
        {isUploading ? (
          /* Spinner */
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            className="inline-block animate-spin"
          >
            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
          </svg>
        ) : (
          /* Image icon */
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="currentColor"
            className="inline-block"
          >
            <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
          </svg>
        )}
      </button>

      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_MIME_TYPES}
        className="hidden"
        onChange={handleFileChange}
      />
    </>
  );
};

export default InsertImageButton;
